import { createApp } from '@backstage/frontend-defaults';
import {
  createFrontendModule,
  createRouteRef,
  PageBlueprint,
} from '@backstage/frontend-plugin-api';
import { SignInPageBlueprint } from '@backstage/plugin-app-react';
import { SignInPage } from '@backstage/core-components';
import { microsoftAuthApiRef } from '@backstage/core-plugin-api';
import Box from '@mui/material/Box';
import HomeIcon from '@mui/icons-material/Home';
import signInBackground from './assets/background_TDF.jpg';
import catalogPlugin from '@backstage/plugin-catalog/alpha';
import rbacPlugin from '@backstage-community/plugin-rbac/alpha';
import { navModule } from './modules/nav';
import { themeModule } from './modules/theme';
import { scaffolderModule } from './modules/scaffolder';
import { HomePage } from './components/home/HomePage';
import { squadK8sModule } from './modules/squadK8s';
import { pandaUsageModule } from './modules/pandaUsage';

import { pandaManageModule } from './modules/pandaManage';
import { devlakeDashboardModule } from './modules/devlakeDashboard';
import { devlakeGithubModule } from './modules/devlakeGithub';
import { devlakeManageModule } from './modules/devlakeManage';
import { temporalManageModule } from './modules/temporalManage';

const signInPage = SignInPageBlueprint.make({
  params: {
    loader: async () => props =>
      (
        // Full-viewport TDF background behind the sign-in card. The core
        // SignInPage has no background prop, so we wrap it in a fixed cover box.
        <Box
          sx={{
            position: 'fixed',
            inset: 0,
            backgroundImage: `url(${signInBackground})`,
            backgroundSize: 'cover',
            backgroundPosition: 'center',
            backgroundRepeat: 'no-repeat',
            overflow: 'auto',
          }}
        >
          <SignInPage
            {...props}
            auto
            provider={{
              id: 'microsoft-auth-provider',
              title: 'Login',
              message: 'Sign in using Microsoft',
              apiRef: microsoftAuthApiRef,
            }}
          />
        </Box>
      ),
  },
});

// Override the home plugin's `page:home` with a dedicated, fixed-layout page.
// The extension ID must be exactly `page:home` to replace the plugin's default
// (the configurable CustomHomepageGrid with "Add widget"). The ID is
// `<kind>:<namespace>[/<name>]`, so we must OMIT `name` here — adding a name
// would produce `page:home/home`, a separate page that does NOT override the
// default. The namespace `home` comes from the module's pluginId.
//
// A `routeRef` is REQUIRED for the page to produce a sidebar nav item: the app
// nav only generates an item for pages that expose a route ref (it skips pages
// without one). The plugin's own root route ref isn't exported, so we define
// our own here.
// `noHeader` lets <HomePage/> own its full layout (it renders its own Page).
// The path defaults to "/" via app-config; title/icon drive the auto-discovered
// "Home" nav item in the sidebar.
const homeRouteRef = createRouteRef();

const customHomePageModule = createFrontendModule({
  pluginId: 'home',
  extensions: [
    PageBlueprint.make({
      params: {
        path: '/',
        routeRef: homeRouteRef,
        noHeader: true,
        title: 'Home',
        icon: <HomeIcon />,
        loader: async () => <HomePage />,
      },
    }),
  ],
});

const app = createApp({
  features: [
    customHomePageModule,
    themeModule,
    catalogPlugin,
    rbacPlugin,
    navModule,
    scaffolderModule,
    squadK8sModule,
    pandaUsageModule,
    pandaManageModule,
    devlakeDashboardModule,
    devlakeGithubModule,
    devlakeManageModule,
    temporalManageModule,
    createFrontendModule({ pluginId: 'app', extensions: [signInPage] }),
  ],
});

export default app.createRoot();
